import React, {
  useState,
  type KeyboardEvent,
  useEffect,
  type ReactElement,
} from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import AssistantViewController, {
  type Instruction,
} from '../AssistantView/AssistantViewController';
import PromptEditor, { type EditorApi } from './PromptEditor';
import styles from './PromptPanel.module.css';
import classnames from 'classnames';
import { uploadFiles } from '../utils/attachmentHandling';
import AttachmentItem from './AttachmentItem';
import { uniqBy } from 'lodash';
import AgentSelection from './AgentSelection';
import { FROZEN_CHAT_ERROR } from '../../constants';
import AiTemplatesModal from '../../../AiTemplatesModal/AiTemplatesModal';
import AhaAnalytics from 'javascripts/analytics';

function truncateName(str, num) {
  if (str.length > num) {
    return str.slice(0, num) + '...';
  } else {
    return str;
  }
}

const Menu = ({ prompts, setShowTemplateModalType, type, onSelect }) => {
  const agentTypeNames = {
    PROMPT: 'request',
    INSTRUCTION: 'instruction',
    AGENT: 'agent',
  };

  return (
    <aha-menu-group>
      <aha-menu-item>
        <aha-menu>
          <div slot='control'>Favorites</div>
          <aha-menu-content>
            {prompts.favorites.length === 0 && (
              <aha-menu-item>
                <div
                  className={styles.emptyState}
                  onClick={() => {
                    setShowTemplateModalType(true);
                    AhaAnalytics.track(
                      'Feature-7530436202602257500',
                      'User opens AI library',
                      {
                        'CTA used': 'AI assistant - Prompt menu',
                      }
                    );
                  }}
                >
                  Select <strong>Search the library</strong> and <br />
                  star ones to add them here.
                </div>
              </aha-menu-item>
            )}
            {prompts.favorites.length > 0 &&
              prompts.favorites.map(option => (
                <aha-menu-item
                  key={option.id}
                  onClick={() => {
                    requestAnimationFrame(() => onSelect(option));
                  }}
                >
                  <label className='tw-justify-between tw-items-center tw-gap-2'>
                    {truncateName(option.name, 30)}

                    <span className={styles.agentType}>
                      {agentTypeNames[option.agentType]}
                    </span>
                  </label>
                </aha-menu-item>
              ))}
          </aha-menu-content>
        </aha-menu>
      </aha-menu-item>
      {(prompts.personal.length > 0 || prompts.workspace.length > 0) && (
        <>
          {prompts.personal.length > 0 && (
            <aha-menu-item>
              <aha-menu>
                <div slot='control'>Personal</div>
                <aha-menu-content>
                  {prompts.personal.map(option => (
                    <aha-menu-item
                      key={option.id}
                      onClick={() => {
                        requestAnimationFrame(() => onSelect(option));
                      }}
                    >
                      <label className='tw-justify-between tw-items-center tw-gap-2'>
                        {truncateName(option.name, 30)}

                        <span className={styles.agentType}>
                          {agentTypeNames[option.agentType]}
                        </span>
                      </label>
                    </aha-menu-item>
                  ))}
                </aha-menu-content>
              </aha-menu>
            </aha-menu-item>
          )}
          {prompts.workspace.length > 0 && (
            <aha-menu-item>
              <aha-menu>
                <div slot='control'>Workspace</div>
                <aha-menu-content>
                  {prompts.workspace.map(option => (
                    <aha-menu-item
                      key={option.id}
                      onClick={() => {
                        requestAnimationFrame(() => onSelect(option));
                      }}
                    >
                      <label className='tw-justify-between tw-items-center tw-gap-2'>
                        {truncateName(option.name, 30)}

                        <span className={styles.agentType}>
                          {agentTypeNames[option.agentType]}
                        </span>
                      </label>
                    </aha-menu-item>
                  ))}
                </aha-menu-content>
              </aha-menu>
            </aha-menu-item>
          )}
        </>
      )}
      <aha-menu-item>
        <a
          href={`/settings/ai_agents/new?tab=prompts&after_view=prompt_panel&show_type_input=true`}
          data-remote='true'
        >
          <i className='fa-regular fa-plus' /> New prompt
        </a>
      </aha-menu-item>
      <aha-menu-item>
        <a
          onClick={() => {
            setShowTemplateModalType(true);
            AhaAnalytics.track(
              'Feature-7530436202602257500',
              'User opens AI library',
              {
                'CTA used': 'AI assistant - Prompt menu',
              }
            );
          }}
        >
          <i className='fa-regular fa-book' /> Search the library
        </a>
      </aha-menu-item>
    </aha-menu-group>
  );
};

const PromptPanel = () => {
  const controller = useController(AssistantViewController);
  const {
    size,
    promptAttachments,
    scopeItems,
    model,
    activeInstructions,
    mappedPrompts,
    showPromptLibrary,
    initialEditorValue,
  } = controller.state;
  const isProcessing = model.active;
  const [prompt, setPrompt] = useState(''); // Prompt currently being edited.
  const [dragHovering, setDragHovering] = useState(false);
  const editorApiRef = React.useRef<EditorApi>(null);
  const canSubmit =
    prompt.trim().length > 0 &&
    !isProcessing &&
    !controller.frozenOrNoUsageAvailable() &&
    promptAttachments.every(a => !a.loading);

  useEffect(() => {
    controller.actionSetEditorApi(editorApiRef.current);
    if (controller.props.focus) {
      // If the prompt editor should be focused on load, then focus it.
      editorApiRef.current?.focus();
    }
  }, [controller]);

  useEffect(() => {
    if (controller.props.initialLibraryOpen) {
      controller.actionResizeByTrigger('full-height');
      controller.actionSetShowPromptLibrary(true);
    }
  }, [controller, controller.props.initialLibraryOpen]);

  const handleSubmit = () => {
    if (canSubmit) {
      controller.actionSubmitPrompt(prompt.trim());
      if (editorApiRef.current) {
        editorApiRef.current.clearContent();
      }
      controller.actionResizeByTrigger('submit-message');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Once the prompt is multi-line then enter will no longer submit and the user
    // must click the button. Cmd+Enter will always submit.
    const canSubmitWithEnter = prompt.trim().split('\n').length <= 1;

    if ((canSubmitWithEnter || e.metaKey) && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
    }
  };

  // When the editor focuses make sure the assistant is grown.
  const handleFocus = () => {
    controller.actionResizeByTrigger('focus-prompt-editor');
  };

  // A click anywhere in the panel should focus the editor.
  const handleClick = () => {
    editorApiRef.current?.focus();
  };

  const handleDrop = event => {
    event.preventDefault();
    setDragHovering(false);
    const files = event.dataTransfer.files;
    if (files) {
      uploadFiles(controller, files);
    }
  };

  let attachments: ReactElement | null = null;
  const imageAttachments = promptAttachments.filter(
    a =>
      a.attachment?.contentType?.startsWith('image/') ||
      a.detectedContentType?.startsWith('image/')
  );

  if (imageAttachments.length > 0) {
    attachments = (
      <div className={styles.attachments}>
        {imageAttachments.map((attachmentStatus, index) => (
          <AttachmentItem key={index} attachmentStatus={attachmentStatus} />
        ))}
      </div>
    );
  }

  // Render non-image attachments in the scope area where they logically belong
  const nonImageAttachments = promptAttachments.filter(
    a =>
      !(
        a.attachment?.contentType?.startsWith('image/') ||
        a.detectedContentType?.startsWith('image/')
      )
  );

  const scope =
    size !== 'toolbar' ? (
      <div className={styles.scopeItems}>
        {Object.values(scopeItems).map((item, index) => (
          <React.Fragment key={index}>{item.render?.()}</React.Fragment>
        ))}
        {nonImageAttachments.map((attachmentStatus, index) => (
          <AttachmentItem key={index} attachmentStatus={attachmentStatus} />
        ))}
      </div>
    ) : null;

  const onSelectInstruction = (instruction: Instruction) => {
    controller.state.activeInstructions = uniqBy(
      [...activeInstructions, instruction],
      'id'
    );
    void controller.trackAiUserAgentUsage(instruction.id);
  };

  const promptMenuItem = (
    <Menu
      prompts={mappedPrompts}
      setShowTemplateModalType={controller.actionSetShowPromptLibrary}
      type='PROMPT'
      onSelect={option => {
        if (option.agentType === 'PROMPT') {
          controller.actionSelectPrompt(option);
          void controller.trackAiUserAgentUsage(option.id);
        } else {
          onSelectInstruction(option);
        }
      }}
    />
  );

  return (
    <div
      className={classnames(styles.promptPanel, {
        [styles.dragOver]: dragHovering,
      })}
      onDrop={event => handleDrop(event)}
      onDragOver={event => event.preventDefault()}
      onDragEnter={() => setDragHovering(true)}
      onDragLeave={event =>
        !event.currentTarget.contains(event.relatedTarget as Node) &&
        setDragHovering(false)
      }
    >
      {showPromptLibrary && (
        <AiTemplatesModal
          templateType={'ALL'}
          category={controller.props.initialLibraryCategory || 'Global_All'}
          placeTemplate={template => {
            if (template.agentType === 'AGENT') {
              controller.actionSetAgent('DefaultAgent');
            }
            controller.actionSelectPrompt({
              prompt: template.markdownInstruction || '',
              id: template.id,
              name: template.name || '',
              agentScope: 'personal',
              favorite: false,
            });
          }}
          closeTemplateModal={() =>
            controller.actionSetShowPromptLibrary(false)
          }
        />
      )}
      {model.emptyChat && (
        <div className={styles.promptToolbar}>
          <div>
            <AgentSelection />
          </div>
        </div>
      )}

      <div
        className={styles.promptBubble}
        onKeyDown={e => handleKeyDown(e)}
        onFocus={() => handleFocus()}
        onClick={() => handleClick()}
      >
        <div className={styles.promptScoller}>
          {scope}
          {attachments}

          <PromptEditor
            initialValue={initialEditorValue}
            setPrompt={prompt => setPrompt(prompt)}
            uploadFiles={files => uploadFiles(controller, files)}
            editorApi={editorApiRef}
            disabled={controller.frozenOrNoUsageAvailable()}
            size={size}
          />
          {model.isChatFrozen && <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>}
        </div>
        <div
          className={classnames(styles.actions, {
            [styles.actionsHidden]: size === 'toolbar',
          })}
        >
          <div className={styles.actionsRight}>
            <aha-menu>
              <aha-button
                kind='default'
                slot='control'
                size='mini'
                disabled={isProcessing ? true : undefined}
              >
                <i className='far fa-plus' />
              </aha-button>

              <aha-menu-content class={styles.menu}>
                <aha-menu-group key='start-chat'>
                  <aha-menu-item onClick={() => controller.actionNewChat()}>
                    <a>
                      <i className='far fa-circle-plus' /> Start a new chat
                    </a>
                  </aha-menu-item>
                </aha-menu-group>
                {controller.projectId && promptMenuItem}
                <aha-menu-group key='upload-files'>
                  <aha-menu-item
                    onClick={() => controller.actionChooseAttachments()}
                  >
                    <a>
                      <i className='far fa-paperclip' /> Upload files & images
                    </a>
                  </aha-menu-item>
                </aha-menu-group>
              </aha-menu-content>
            </aha-menu>

            {!isProcessing && (
              <>
                <aha-button
                  kind='primary'
                  size='mini'
                  onClick={handleSubmit}
                  disabled={!canSubmit ? true : undefined}
                >
                  <i className='far fa-arrow-up' />
                </aha-button>
                {model.isChatFrozen && (
                  <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
                )}
              </>
            )}
            {isProcessing && (
              <aha-button
                kind='default'
                size='mini'
                onClick={() => model.cancel()}
              >
                <i className='fa-solid fa-stop' /> Stop
              </aha-button>
            )}
          </div>
        </div>
        <div className={styles.promptDropHover}>
          <div>
            <i className='far fa-image' />
            &nbsp; Drop files here to add them to the chat
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationView(PromptPanel);
